import type {
  ReservationCartProduct,
  WeightSpecial,
} from '@iheartjane/data-access';
import type { PriceId, ProductKind } from '@iheartjane/types';
import round from 'lodash-es/round';

import { defaultWeightIdForMenuProduct } from '../storeMenu';
import { maybeParseFloat } from './maybeParseFloat';
import type { WeightPriced } from './weightPriced';

type WeightSpecialPriced = {
  special_id?: number | null;
} & Partial<Record<`special_price_${PriceId}`, WeightSpecial | null>>;

export const getUnitPrice = (
  menuProduct: WeightPriced,
  priceId: PriceId
): number | undefined => maybeParseFloat(menuProduct[`price_${priceId}`]);

export const getDiscountedUnitPrice = (
  menuProduct: WeightPriced,
  priceId: PriceId
): number | undefined =>
  maybeParseFloat(menuProduct[`discounted_price_${priceId}`]);

export const isDiscountSupported = (
  menuProduct: WeightSpecialPriced & object,
  priceId: PriceId,
  supportedSpecials: { id: number }[] = []
): boolean | undefined => {
  const specialPrice = menuProduct[`special_price_${priceId}`];
  const specialId = menuProduct.special_id;
  const supportedSpecialIds = supportedSpecials.map(({ id }) => id);

  const hasSpecialPrice =
    specialPrice?.special_id &&
    supportedSpecialIds?.includes(specialPrice?.special_id);

  const hasValidSpecialId =
    specialId && supportedSpecialIds.includes(specialId);

  return Boolean(hasSpecialPrice || hasValidSpecialId);
};

export const calculateTotalPrice = (
  unitPrice: number | undefined,
  count: number
) => {
  if (unitPrice === undefined) return undefined;

  return unitPrice * count;
};

export const getOriginalAndDiscountedTotalPrices = ({
  menuProduct,
  count = 1,
  priceId,
}: {
  count?: number;
  menuProduct: WeightPriced & { kind: ProductKind };
  priceId?: PriceId;
}) => {
  const selectedWeight =
    priceId || defaultWeightIdForMenuProduct({ menuProduct });
  const originalUnitPrice = getUnitPrice(menuProduct, selectedWeight);
  const discountUnitPrice = getDiscountedUnitPrice(menuProduct, selectedWeight);
  const originalPrice = calculateTotalPrice(originalUnitPrice, count);
  const discountedPrice = calculateTotalPrice(discountUnitPrice, count);

  return { originalPrice, discountedPrice };
};

export const getTotalOriginalAndDiscountedPrices = ({
  menuProduct,
  cartProduct,
}: {
  cartProduct: ReservationCartProduct[];
  menuProduct: WeightPriced;
}) => {
  const withPrices = cartProduct.map((product) => {
    const originalUnitPrice = getUnitPrice(menuProduct, product.price_id);
    const discountUnitPrice = getDiscountedUnitPrice(
      menuProduct,
      product.price_id
    );
    return {
      originalPrice: calculateTotalPrice(originalUnitPrice, product.count),
      discountedPrice: calculateTotalPrice(discountUnitPrice, product.count),
    };
  });

  const { totalOriginalPrice, totalDiscountedPrice } = withPrices.reduce(
    (prev, curr) => ({
      totalOriginalPrice: prev.totalOriginalPrice + (curr.originalPrice || 0),
      totalDiscountedPrice:
        prev.totalDiscountedPrice + (curr.discountedPrice || 0),
    }),
    { totalOriginalPrice: 0, totalDiscountedPrice: 0 }
  );

  return { totalOriginalPrice, totalDiscountedPrice };
};

export const calculateDiscount = (
  originalPrice: number,
  discountedPrice: number
) => round(originalPrice - discountedPrice, 2);

export const calculateDiscountPercentage = (
  originalPrice: number | undefined,
  discountedPrice: number | undefined
) => {
  if (!discountedPrice || !originalPrice || discountedPrice === originalPrice) {
    return null;
  }
  return round(((originalPrice - discountedPrice) / originalPrice) * 100);
};
